/**
 * Auth endpoints — live API only (the mock tree needs no session).
 *
 * Every call is deepdive-v2's v3 auth (`/v3/auth/*`). Its tokens open the v3
 * routes and every v2 route the app still reads (settings, quick search…).
 *
 * - POST /v3/auth/login { email, password } → { accessToken, refreshToken, user };
 * - POST /v3/auth/refresh — called by the base query on a 401, never from here;
 * - POST /v3/auth/logout { refreshToken } — deletes that v3 session, so the
 *   refresh token stops working. Needs no access token: an expired session
 *   can still sign out.
 */
import { z } from 'zod/mini';

import { parseAuthTokens, type AuthTokens } from '@/services/authTokens';
import { api, overrideExisting } from '@/store/api/api';

export const LoginRequest = z.object({
  email: z.pipe(z.string().check(z.trim()), z.email('Enter a valid email address')),
  password: z.string().check(z.minLength(1, 'Enter your password')),
});
export type LoginRequest = z.infer<typeof LoginRequest>;

export type LoginResponse = AuthTokens;

export const authApi = api.injectEndpoints({
  overrideExisting,
  endpoints: (build) => ({
    login: build.mutation<LoginResponse, LoginRequest>({
      query: (body) => ({ url: '/v3/auth/login', method: 'POST', body }),
      transformResponse: (raw: unknown) => parseAuthTokens(raw),
    }),
    logout: build.mutation<void, { refreshToken: string }>({
      query: ({ refreshToken }) => ({
        url: '/v3/auth/logout',
        method: 'POST',
        body: { refreshToken },
      }),
      transformResponse: () => undefined,
    }),
  }),
});
